import React, { Fragment, useEffect, useState } from "react";
import Breadcrumb from "../../../../components/common/breadcrumb";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import DataTable from "react-data-table-component";
import { CSVLink } from "react-csv";
import { showToast } from "../../../../components/ToastifyNotification";
import { UncontrolledTooltip } from "reactstrap";
import Swal from "sweetalert2";

import { DeletePurchaseBillReturn, GetPurchaseBillReturns } from "../../../../api/medicalStore/transaction/purchaseBillReturnAPI";

const ListPurchaseBillReturnEntry = () => {
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [search, setSearch] = useState("");

  const navigate = useNavigate();
  const dispatch = useDispatch();

  /* ================= FETCH DATA ================= */
  const fetchData = async () => {
    dispatch({ type: "LOADER_START" });
    try {
      const response = await GetPurchaseBillReturns();

      if (response.success) {
        showToast("success", response.message);
        const formattedData = response.data.map((item, index) => ({
          index: index + 1,
          id: item._id,

          vrType: item.vrType || "",
          vrNo: item.vrNo || "",
          vrDate: item.vrDate
            ? new Date(item.vrDate).toLocaleDateString()
            : "",

          supplierName: item.supplierName || "",
          partyBillNo: item.partyBillNo || "",
          billDate: item.billDate
            ? new Date(item.billDate).toLocaleDateString()
            : "",

          department: item.departmentId?.name || "-",

          totalNoOfPacks: item.totalNoOfPacks || 0,
          gst: item.gst || 0,
          grossAmount: item.grossAmount || 0,
          discountAmount: item.discountAmount || 0,
          roundOff: item.roundOff || 0,
          netAmount: item.netAmount || 0,

          createdBy: item.createdBy?.name || "-",
          createdAt: new Date(item.createdAt).toLocaleDateString()
        }));

        setData(formattedData);
        setFilteredData(formattedData);
      } else {
        showToast("error", response.message);
      }
    } catch (error) {
      showToast("error", error.message || "Something went wrong");
    } finally {
      dispatch({ type: "LOADER_STOP" });
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  /* ================= SEARCH ================= */
  useEffect(() => {
    const results = data.filter(item =>
      Object.values(item)
        .join(" ")
        .toLowerCase()
        .includes(search.toLowerCase())
    );
    setFilteredData(results);
  }, [search, data]);

  /* ================= ACTIONS ================= */
  const handleEditClick = id => {
    navigate(
      `/medical-store/transaction/purchase-bill-return-entry/edit/${id}`
    );
  };

  const handleDeleteClick = id => {
    Swal.fire({
      title: "Are you sure?",
      text: "This will soft delete the purchase bill!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!"
    }).then(async result => {
      if (result.isConfirmed) {
        dispatch({ type: "LOADER_START" });
        try {
          const response = await DeletePurchaseBillReturn(id);
          if (response.success) {
            showToast("success", response.message);
            setData(prev => prev.filter(item => item.id !== id));
            setFilteredData(prev => prev.filter(item => item.id !== id));
          } else {
            showToast("error", response.message);
          }
        } catch (error) {
          showToast("error", error.message);
        } finally {
          dispatch({ type: "LOADER_STOP" });
        }
      }
    });
  };

  /* ================= TABLE COLUMNS ================= */
  const columns = [
    { name: "S.No", selector: row => row.index, width: "8%", sortable: true },
    { name: "VR Type", selector: row => row.vrType, width: "10%", sortable: true },
    { name: "VR No", selector: row => row.vrNo, sortable: true, width: "10%" },
    { name: "VR Date", selector: row => row.vrDate, sortable: true, width: "10%" },
    {
      name: "Supplier",
      selector: row => row.supplierName,
      sortable: true,
      width: "10%"
    },
    { name: "Party Bill No", selector: row => row.partyBillNo, sortable: true, width: "10%" },
    { name: "Bill Date", selector: row => row.billDate, sortable: true, width: "10%" },
    { name: "Department", selector: row => row.department, sortable: true, width: "10%" },
    {
      name: "Packs",
      selector: row => row.totalNoOfPacks,
      sortable: true, 
      width: "10%"
    },
    { name: "GST", selector: row => row.gst, sortable: true, width: "10%" },
    {
      name: "Gross",
      selector: row => row.grossAmount,
      sortable: true,
      width: "10%"
    },
    {
      name: "Discount",
      selector: row => row.discountAmount,
      sortable: true,
      width: "10%"
    },
    {
      name: "Round Off",
      selector: row => row.roundOff,
      sortable: true, 
      width: "10%"
    },
    {
      name: "Net Amount",
      selector: row => row.netAmount,
      sortable: true, 
      width: "10%"
    },
    { name: "Created By", selector: row => row.createdBy, sortable: true, width: "10%" },
    {
      name: "Actions",
      cell: row => (
        <div>
          <button
            className="btn btn-primary me-2"
            onClick={() => handleEditClick(row.id)}
            id={`edit-${row.id}`}
          >
            <i className="fa fa-pencil-square-o" />
          </button>
          <UncontrolledTooltip target={`edit-${row.id}`}>
            Edit
          </UncontrolledTooltip>

          <button
            className="btn btn-danger"
            onClick={() => handleDeleteClick(row.id)}
            id={`delete-${row.id}`}
          >
            <i className="fa fa-trash-o" />
          </button>
          <UncontrolledTooltip target={`delete-${row.id}`}>
            Delete
          </UncontrolledTooltip>
        </div>
      ),
      ignoreRowClick: true,
      button: true,
      width: "15%"
    }
  ];

  /* ================= CSV EXPORT ================= */
  const headers = [
    { label: "S.No", key: "index" },
    { label: "VR Type", key: "vrType" },
    { label: "VR No", key: "vrNo" },
    { label: "VR Date", key: "vrDate" },
    { label: "Supplier Name", key: "supplierName" },
    { label: "Party Bill No", key: "partyBillNo" },
    { label: "Bill Date", key: "billDate" },
    { label: "Department", key: "department" },
    { label: "Total Packs", key: "totalNoOfPacks" },
    { label: "GST", key: "gst" },
    { label: "Gross Amount", key: "grossAmount" },
    { label: "Discount Amount", key: "discountAmount" },
    { label: "Round Off", key: "roundOff" },
    { label: "Net Amount", key: "netAmount" },
    { label: "Created By", key: "createdBy" },
    { label: "Created At", key: "createdAt" }
  ];

  const ExportCSV = ({ data }) => (
    <CSVLink data={data} headers={headers} filename="purchase-bill-returns.csv">
      <button className="btn btn-primary">Export</button>
    </CSVLink>
  );

  /* ================= UI ================= */
  return (
    <Fragment>
      <Breadcrumb parent="Medical Store" title="Purchase Bill Returns" />

      <div className="container-fluid">
        <div className="card">
          <div className="card-header">
            <div className="d-flex justify-content-between">
              <h5>Purchase Bill Return List</h5>
              <Link
                to={`/medical-store/transaction/purchase-bill-return-entry/add`}
              >
                <button className="btn btn-primary">
                  Create Purchase Bill Return
                </button>
              </Link>
            </div>

            <div className="row mt-2">
              <div className="col-sm-6">
                <input
                  className="form-control"
                  placeholder="Search Purchase Bill Return..."
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                />
              </div>
              <div className="col-sm-6 text-end">
                <ExportCSV data={data} />
              </div>
            </div>
          </div>

          <div className="card-body">
            <DataTable
              columns={columns}
              data={filteredData}
              pagination
              highlightOnHover
              dense
            />
          </div>
        </div>
      </div>
    </Fragment>
  );
};

export default ListPurchaseBillReturnEntry;
